import { MapPin, Phone, Mail, MessageCircle, Building2, Globe2 } from "lucide-react";
import { PageMeta } from "../components/PageMeta";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { EnquiryForm } from "../components/EnquiryForm";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES, CONTACT, INDIA_OFFICE } from "../data/content";

export default function Contact() {
  const { t, isAr } = useLanguage();
  const company = isAr ? CONTACT.companyAr : CONTACT.company;
  const address = isAr ? CONTACT.addressAr : CONTACT.address;

  const CARDS = [
    { icon: MapPin, label: t("contactPage.visit"), lines: [company, address], testId: "contact-address-card" },
    { icon: Phone, label: t("contactPage.call"), lines: [CONTACT.phone], href: CONTACT.phoneHref, testId: "contact-phone-card", ltr: true },
    { icon: Mail, label: t("contactPage.email"), lines: [CONTACT.email], href: CONTACT.emailHref, testId: "contact-email-card", ltr: true },
  ];

  return (
    <main>
      <PageMeta
        title="Contact Al Nama Importer | B2B Food Trading Dubai, UAE"
        description="Get in touch with Al Nama Importer for product, sourcing, import or export requirements. Compass Building, Al Hamra Industrial Zone, Ras Al Khaimah, UAE. +971 50 379 2861."
        titleAr="اتصل بنا | النعم للاستيراد – تجارة الأغذية بين الشركات في دبي"
        descriptionAr="تواصل مع النعم للاستيراد لمتطلبات المنتجات أو التوريد أو الاستيراد أو التصدير. مبنى كومباس، منطقة الحمرا الصناعية، رأس الخيمة، الإمارات العربية المتحدة. +971 50 379 2861."
      />
      <PageHero
        image={IMAGES.spiceMarket}
        overline={t("contactPage.heroOver")}
        titleLines={[t("contactPage.heroL1"), t("contactPage.heroL2")]}
        text={t("contactPage.heroText")}
      />

      <section className="bg-[#09121F] py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.3fr]">
          <div className="space-y-5">
            {CARDS.map((c, i) => {
              const Inner = (
                <>
                  <c.icon className="h-6 w-6 shrink-0 text-amber-400" strokeWidth={1.4} />
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] font-display font-semibold text-amber-400">
                      {c.label}
                    </p>
                    {c.lines.map((l, j) => (
                      <p
                        key={j}
                        dir={c.ltr ? "ltr" : undefined}
                        className={`mt-1.5 text-sm text-slate-300/90 ${j === 0 && c.lines.length > 1 ? "font-medium text-emerald-50" : ""} ${c.ltr ? "rtl:text-right" : ""}`}
                      >
                        {l}
                      </p>
                    ))}
                  </div>
                </>
              );
              return (
                <Reveal key={c.label} delay={i * 0.08}>
                  {c.href ? (
                    <a
                      data-testid={c.testId}
                      href={c.href}
                      className="flex items-start gap-5 rounded-lg border border-amber-500/15 bg-[#0F1E18]/60 p-6 transition-all duration-500 hover:border-amber-500/45 hover:bg-[#0F1E18]"
                    >
                      {Inner}
                    </a>
                  ) : (
                    <div data-testid={c.testId} className="flex items-start gap-5 rounded-lg border border-amber-500/15 bg-[#0F1E18]/60 p-6">
                      {Inner}
                    </div>
                  )}
                </Reveal>
              );
            })}

            <Reveal delay={0.28}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <a
                  data-testid="contact-whatsapp-btn"
                  href={CONTACT.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-[#1EBE5D] px-4 py-3.5 font-display text-xs font-semibold uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:scale-[1.03]"
                >
                  <MessageCircle className="h-4 w-4" /> {t("contactPage.whatsapp")}
                </a>
                <a
                  data-testid="contact-call-btn"
                  href={CONTACT.phoneHref}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-amber-500/40 px-4 py-3.5 font-display text-xs font-semibold uppercase tracking-[0.15em] text-amber-400 transition-colors duration-300 hover:bg-amber-500/10"
                >
                  <Phone className="h-4 w-4" /> {t("contactPage.callBtn")}
                </a>
                <a
                  data-testid="contact-email-btn"
                  href={CONTACT.emailHref}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-amber-500/40 px-4 py-3.5 font-display text-xs font-semibold uppercase tracking-[0.15em] text-amber-400 transition-colors duration-300 hover:bg-amber-500/10"
                >
                  <Mail className="h-4 w-4" /> {t("contactPage.emailBtn")}
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div
              data-testid="contact-enquiry-form-card"
              className="rounded-xl border border-amber-500/25 bg-[#0A1622] p-6 shadow-2xl sm:p-10"
            >
              <p className="text-xs uppercase tracking-[0.28em] font-display font-semibold text-amber-400">
                {t("contactPage.formOver")}
              </p>
              <h2 className="mt-3 font-serif text-2xl font-semibold text-emerald-50 sm:text-3xl">
                {t("contactPage.formTitle")}
              </h2>
              <div className="mt-8">
                <EnquiryForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="offices-section" className="bg-[#0B1D16] py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            overline={t("contactPage.officesOver")}
            title={
              <>
                {t("contactPage.officesTitleA")} <span className="italic text-gold-gradient">{t("contactPage.officesTitleB")}</span>
              </>
            }
          />
          <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Reveal>
              <div
                data-testid="uae-office-card"
                className="flex h-full flex-col rounded-lg border border-amber-500/20 bg-[#0F1E18]/60 p-8"
              >
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-amber-400" strokeWidth={1.5} />
                  <p className="text-xs uppercase tracking-[0.25em] font-display font-semibold text-amber-400">
                    {t("contactPage.uaeOffice")}
                  </p>
                </div>
                <h3 className="mt-4 font-serif text-2xl font-semibold text-emerald-50">{company}</h3>
                <p className="mt-3 flex items-start gap-2.5 text-sm font-light leading-relaxed text-slate-300/85">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-500/70" />
                  {address}
                </p>
                <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 border-t border-amber-500/10 pt-5 text-sm">
                  <a href={CONTACT.phoneHref} className="flex items-center gap-2 text-slate-300 transition-colors hover:text-amber-400" dir="ltr">
                    <Phone className="h-4 w-4 text-amber-500/70" /> {CONTACT.phone}
                  </a>
                  <a href={CONTACT.emailHref} className="flex items-center gap-2 text-slate-300 transition-colors hover:text-amber-400">
                    <Mail className="h-4 w-4 text-amber-500/70" /> {CONTACT.email}
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <div
                data-testid="india-office-card"
                className="flex h-full flex-col rounded-lg border border-amber-500/20 bg-[#0F1E18]/60 p-8"
              >
                <div className="flex items-center gap-3">
                  <Globe2 className="h-5 w-5 text-amber-400" strokeWidth={1.5} />
                  <p className="text-xs uppercase tracking-[0.25em] font-display font-semibold text-amber-400">
                    {t("contactPage.indiaOffice")}
                  </p>
                </div>
                <div className="mt-5 flex items-center gap-5">
                  <img
                    data-testid="india-ceo-photo"
                    src="/assets/india-ceo.png"
                    alt={isAr ? INDIA_OFFICE.pocAr : INDIA_OFFICE.poc}
                    className="h-20 w-20 rounded-full border-2 border-amber-500/50 object-cover object-top shadow-[0_0_24px_rgba(212,175,55,0.2)]"
                  />
                  <div>
                    <h3 className="font-serif text-2xl font-semibold text-emerald-50">
                      {isAr ? INDIA_OFFICE.pocAr : INDIA_OFFICE.poc}
                    </h3>
                    <p className="mt-0.5 font-display text-xs font-semibold uppercase tracking-[0.18em] text-amber-400">
                      {t("contactPage.ceoTitle")}
                    </p>
                    <p className="mt-1 text-sm text-slate-400">{INDIA_OFFICE.label}</p>
                  </div>
                </div>
                <p className="mt-4 text-xs uppercase tracking-[0.2em] text-slate-500">{t("contactPage.indiaTag")}</p>
                <p className="mt-3 flex items-start gap-2.5 text-sm font-light leading-relaxed text-slate-300/85">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-500/70" />
                  {isAr ? INDIA_OFFICE.addressAr : INDIA_OFFICE.address}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-amber-500/10 pt-5">
                  <a
                    data-testid="india-office-call-btn"
                    href={INDIA_OFFICE.phoneHref}
                    className="inline-flex items-center gap-2 rounded-md border border-amber-500/40 px-4 py-2.5 font-display text-xs font-semibold uppercase tracking-[0.15em] text-amber-400 transition-colors duration-300 hover:bg-amber-500/10"
                    dir="ltr"
                  >
                    <Phone className="h-3.5 w-3.5" /> {INDIA_OFFICE.phone}
                  </a>
                  <a
                    data-testid="india-office-whatsapp-btn"
                    href={INDIA_OFFICE.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-md bg-[#1EBE5D] px-4 py-2.5 font-display text-xs font-semibold uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:scale-[1.03]"
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> {t("contactPage.whatsapp")}
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
